import type { StageName } from '../../lib/collections';
import LogoBuilder from '../../components/launchpad/homework/LogoBuilder';
import ProblemDiary from '../../components/launchpad/homework/ProblemDiary';
import CEOContract from '../../components/launchpad/homework/CEOContract';
import FriendSurvey from '../../components/launchpad/homework/FriendSurvey';
import WouldYouRather from '../../components/launchpad/homework/WouldYouRather';
import ElevatorPitch from '../../components/launchpad/homework/ElevatorPitch';
import { useDarkStore } from '../store/darkStore';

interface StageHomeworkProps {
  stageKey: StageName;
}

/**
 * Renders the matching homework component for a stage, wired to the
 * darkStore's homework slice. The legacy components are designed for a
 * light background — we wrap them in a white panel so they read on both
 * dark and light themes.
 */
export default function StageHomework({ stageKey }: StageHomeworkProps) {
  const hw = useDarkStore((s) => s.homework);
  const setHomework = useDarkStore((s) => s.setHomework);

  let body: React.ReactNode = null;

  switch (stageKey) {
    case 'ideaExplosion':
      body = (
        <LogoBuilder
          initialKeywords={hw.logoKeywords}
          initialColors={hw.selectedLogoColors}
          initialShape={hw.selectedLogoShape}
          onSaveKeywords={(v) => setHomework('logoKeywords', v)}
          onSaveColors={(v) => setHomework('selectedLogoColors', v)}
          onSaveShape={(v) => setHomework('selectedLogoShape', v)}
        />
      );
      break;
    case 'problemSolver':
      body = (
        <ProblemDiary
          initialEntries={hw.problemDiaryEntries}
          onSave={(v) => setHomework('problemDiaryEntries', v)}
        />
      );
      break;
    case 'customerDetective':
      body = (
        <CEOContract
          initialBigPromise={hw.ceoBigPromise}
          initialCustomerFeeling={hw.ceoCustomerFeeling}
          initialCeoOath={hw.ceoCeoOath}
          onSaveBigPromise={(v) => setHomework('ceoBigPromise', v)}
          onSaveCustomerFeeling={(v) => setHomework('ceoCustomerFeeling', v)}
          onSaveCeoOath={(v) => setHomework('ceoCeoOath', v)}
        />
      );
      break;
    case 'mvpMadness':
      body = (
        <FriendSurvey
          initialResponses={hw.friendSurveyResponses}
          onSave={(v) => setHomework('friendSurveyResponses', v)}
        />
      );
      break;
    case 'moneyMoves':
      body = (
        <WouldYouRather
          initialItems={hw.wouldYouRatherItems}
          initialProfitCalc={hw.profitCalculation}
          onSaveItems={(v) => setHomework('wouldYouRatherItems', v)}
          onSaveProfitCalc={(v) => setHomework('profitCalculation', v)}
        />
      );
      break;
    case 'competitionStation':
      body = (
        <ElevatorPitch
          initialWho={hw.elevatorPitchWho}
          initialProblem={hw.elevatorPitchProblem}
          initialSolution={hw.elevatorPitchSolution}
          initialWhy={hw.elevatorPitchWhy}
          onSaveWho={(v) => setHomework('elevatorPitchWho', v)}
          onSaveProblem={(v) => setHomework('elevatorPitchProblem', v)}
          onSaveSolution={(v) => setHomework('elevatorPitchSolution', v)}
          onSaveWhy={(v) => setHomework('elevatorPitchWhy', v)}
        />
      );
      break;
    default:
      body = null;
  }

  // The legacy homework components are themed for light backgrounds — wrap
  // them in a white, rounded panel so they read cleanly in both themes.
  return (
    <div className="overflow-hidden rounded-2xl bg-white p-1 text-gray-900 shadow-inner ring-1 ring-gray-200 dark:ring-white/10">
      {body}
    </div>
  );
}
